import { useEffect, useMemo, useState } from 'react';
import { Box, Alert, Switch, FormControlLabel } from '@mui/material';
import { TextField } from '../@core/components/mui';
import { PageFormLayout } from '../components/layouts/PageFormLayout';
import { settingsService } from '../services/settingsService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import type { ProjectSettingsDto, UpdateProjectSettingsRequest } from '../types/settings';

/**
 * Project settings page.
 */
export const ProjectSettingsPage = () => {
  const { currentTenant, currentProject } = useContextStore();
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ProjectSettingsDto | null>(null);

  const initialForm = useMemo<UpdateProjectSettingsRequest>(
    () => ({
      maxAgentsPerProject: null,
      maxSessionsPerDay: null,
      maxStorageBytes: null,
      monthlyBudgetLimit: null,
      budgetAlertsEnabled: true,
      budgetAlertThreshold: 0.8,
      notificationSettingsJson: null,
      resourceLimitsJson: null,
      metadataJson: null,
    }),
    []
  );

  const [form, setForm] = useState<UpdateProjectSettingsRequest>(initialForm);

  const load = async () => {
    if (!currentProject) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const settings = await settingsService.getProjectSettings(currentProject.id);
      setData(settings);
      setForm({
        maxAgentsPerProject: settings.maxAgentsPerProject ?? null,
        maxSessionsPerDay: settings.maxSessionsPerDay ?? null,
        maxStorageBytes: settings.maxStorageBytes ?? null,
        monthlyBudgetLimit: settings.monthlyBudgetLimit ?? null,
        budgetAlertsEnabled: settings.budgetAlertsEnabled,
        budgetAlertThreshold: settings.budgetAlertThreshold ?? 0.8,
        notificationSettingsJson: settings.notificationSettingsJson ?? null,
        resourceLimitsJson: settings.resourceLimitsJson ?? null,
        metadataJson: settings.metadataJson ?? null,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load project settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [currentProject?.id]);

  const save = async () => {
    if (!currentProject) return;

    setSaving(true);
    setError(null);
    try {
      const updated = await settingsService.updateProjectSettings(currentProject.id, form);
      setData(updated);
      addNotification('Project settings saved', 'success');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save project settings';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!currentTenant) {
    return (
      <Box sx={{ p: 6 }}>
        <Alert severity="info">Please select a tenant first to view project settings.</Alert>
      </Box>
    );
  }

  if (!currentProject) {
    return (
      <Box sx={{ p: 6 }}>
        <Alert severity="info">Please select a project first to view project settings.</Alert>
      </Box>
    );
  }

  const subtitle = [
    `Tenant: ${currentTenant.name}`,
    `Project: ${currentProject.name}`,
    ...(data?.projectId ? [data.projectId] : []),
  ].join(' \u2022 ');

  return (
    <PageFormLayout
      title="Project Settings"
      subtitle={subtitle}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      actions={{
        onSave: save,
        onCancel: () => window.history.back(),
        saving,
      }}
      sections={[
        {
          id: 'limits',
          title: 'Limits',
          content: (
            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
              <TextField
                label="Max agents per project"
                size="small"
                type="number"
                value={form.maxAgentsPerProject ?? ''}
                onChange={(e) =>
                  setForm((s) => ({ ...s, maxAgentsPerProject: e.target.value === '' ? null : Number(e.target.value) }))
                }
                disabled={loading}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Max sessions per day"
                size="small"
                type="number"
                value={form.maxSessionsPerDay ?? ''}
                onChange={(e) =>
                  setForm((s) => ({ ...s, maxSessionsPerDay: e.target.value === '' ? null : Number(e.target.value) }))
                }
                disabled={loading}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Max storage (bytes)"
                size="small"
                type="number"
                value={form.maxStorageBytes ?? ''}
                onChange={(e) =>
                  setForm((s) => ({ ...s, maxStorageBytes: e.target.value === '' ? null : Number(e.target.value) }))
                }
                disabled={loading}
                inputProps={{ min: 0 }}
              />
            </Box>
          ),
        },
        {
          id: 'budget',
          title: 'Budget',
          content: (
            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
              <TextField
                label="Monthly budget limit"
                size="small"
                type="number"
                value={form.monthlyBudgetLimit ?? ''}
                onChange={(e) =>
                  setForm((s) => ({ ...s, monthlyBudgetLimit: e.target.value === '' ? null : Number(e.target.value) }))
                }
                disabled={loading}
                inputProps={{ min: 0, step: 0.01 }}
              />
              <TextField
                label="Budget alert threshold (0 - 1)"
                size="small"
                type="number"
                value={form.budgetAlertThreshold ?? ''}
                onChange={(e) =>
                  setForm((s) => ({ ...s, budgetAlertThreshold: e.target.value === '' ? null : Number(e.target.value) }))
                }
                disabled={loading || !form.budgetAlertsEnabled}
                inputProps={{ min: 0, max: 1, step: 0.01 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.budgetAlertsEnabled}
                    onChange={(e) => setForm((s) => ({ ...s, budgetAlertsEnabled: e.target.checked }))}
                  />
                }
                label="Enable budget alerts"
              />
            </Box>
          ),
        },
      ]}
    />
  );
};
